import { ecLean, itemField, record, rollTraces, scalar, upper } from '../ability/context';
import type { AbilityUseVerdict } from './ability-use';
import { inventoryField } from '../../shared/array-types';
import { statusItemId, statusRows } from '../statuses/expiry';
import { selectCreaturePicture, creaturePictureField } from '../../shared/creature-token-geometry';
import { ftClockCampaignField } from '../../shared/campaign-clock';
import { ORB_TYPES } from '../ability/orbs/orb-queue';
import { CRIT_CASCADE_RULES } from './weapon-attack-cascades';
import { resolveRollVfxRecipe } from '../presentation/roll-vfx-cue';
import { assertAmmunitionValues } from './ammunition-value-contract';

type Row = Record<string, unknown>;
type Resolved = Extract<AbilityUseVerdict, { kind: 'resolved' }>;
type Command = { operation_id: string; encounter_id: string; actor_id: string; ability_id: string; roll_private: number; verdict: Resolved };
export interface AbilityFrameReaders {
  campaign: Row | null;
  definition: (id: string) => Row | null | undefined;
  inventory: (id: string) => Row | null | undefined;
  member: (id: string) => Row | null | undefined;
}

/** Serialize the already-decided one-unit consumption through the existing fixed inventory saver.
 * Lean inventory omits quantity one and deleted zero; the page's inventory reader owns those defaults. */
export function prepareAbilityCommitRows(command: Command, consumed: { inventoryId: string } | undefined, read: AbilityFrameReaders): void {
  // The native form planner already selected these exact columns and before values.
  if (command.verdict.shapeshiftInventory?.length) {
    const delta = command.verdict.timedInventory ??= { updates: [], creates: [], removals: [] };
    for (const write of command.verdict.shapeshiftInventory) {
      const column = write.kind === 'unequip_acquired' ? 'equipped' : 'inventory_private';
      const after = write.kind === 'unequip_acquired' ? '' : write.kind === 'absorb' ? 2 : 0;
      delta.updates.push({ inventory_id: write.inventory_id, before: { [column]: write.before }, changes: { [column]: after } });
    }
  }
  if (!consumed) return;
  const id = upper(consumed.inventoryId), source = read.inventory(id);
  if (!source) throw new Error('charged_cast:source-not-held');
  const qty = Number(inventoryField(source, 'item_qty') ?? 1);
  const after = { item_qty: Math.max(0, qty - 1), inventory_deleted: qty <= 1 ? 1 : 0 };
  const verdict = command.verdict;
  verdict.weaponInventoryChanges ??= [];
  verdict.weaponInventoryChanges.push({ inventory_id: id, before: { possess_id: String(inventoryField(source, 'possess_id') ?? ''),
    item_qty: qty, inventory_deleted: Number(inventoryField(source, 'inventory_deleted') ?? 0) }, after, log_type: 'inventory_deleted' });
  if (after.inventory_deleted) for (const status of statusRows(source, 'target')) {
    const statusId = upper(scalar(status.status_id));
    if (verdict.statusesRemoved.some(s => upper(s.status_id) === statusId)) continue;
    verdict.statusesRemoved.push({ status_id: statusId, status_item_id: statusItemId(status), target: id,
      before: { status_target_id: id, status_item_id: statusItemId(status),
        status_caster_id: String(scalar(status.status_caster_id) ?? ''), status_connected_id: String(scalar(status.status_connected_id) ?? ''),
        status_deleted: Number(scalar(status.status_deleted) ?? 0) } });
  }
  verdict.rows.push({ roll_type_1: 'inventory_deleted', roll_type_2: 'consumed', roll_item_id: id,
    roll_by: command.actor_id, roll_to: null, roll_result: 1, roll_private: 0 });
}

/** Presentation and clock snapshots previously manufactured by the PHP commit. Only touched rows are visited. */
export function serializeAbilityCommitFrame(command: Command, read: AbilityFrameReaders): void {
  assertAmmunitionValues(command.verdict.weaponInventoryChanges);
  const transport = command as unknown as Row, verdict = command.verdict;
  const clock: Row = {};
  for (const unit of ['year', 'month', 'day', 'hour', 'minute', 'second'])
    clock['campaign_time_' + unit] = ftClockCampaignField(read.campaign, 'campaign_time_' + unit) ?? 0;
  transport.campaign_snapshot = { campaigns: clock };
  const counts = new Map<string, number>();
  const inventory = new Map<string, Row | null>();
  const inventoryFor = (id: string): Row | null => { if (!inventory.has(id)) inventory.set(id, read.inventory(id) ?? null); return inventory.get(id)!; };
  transport.log_items = verdict.rows.map(r => {
    if (r.item_display) return r.item_display;
    const id = upper(r.roll_item_id), inv = inventoryFor(id);
    const used = r.house_rule || r.roll_type_1 === 'concentration_broken' ? command.ability_id : id;
    const definition = read.definition(used) ?? inv ?? null;
    let name = String((r.roll_type_1 === 'inventory_deleted' ? inventoryField(inv, 'inventory_name') : null) ?? itemField(definition, 'item_name') ?? inventoryField(inv, 'inventory_name') ?? '');
    let type = String(itemField(definition, 'item_type') ?? '');
    if (r.roll_type_1 === 'orb_channeled') {
      const entry = ORB_TYPES.find(o => upper(o.itemId) === id);
      if (entry) { name = entry.name; type = 'orb_type'; }
    }
    if (r.roll_type_1 === 'status_stack_increased') for (const stack of verdict.statusStacks ?? []) {
      if (upper(stack.target) !== upper(r.roll_to) || upper(stack.status_item_id) !== id) continue;
      const count = Math.min(stack.status_stack, (counts.get(stack.status_id) ?? stack.before) + 1);
      counts.set(stack.status_id, count); name += ` (${count})`; break;
    }
    return { item_name: name, item_type: type };
  });
  transport.log_item_layers = verdict.rows.map(r => {
    const inv = inventoryFor(upper(r.roll_item_id));
    if (!inv) return null;
    const write = verdict.weaponInventoryChanges?.find(w => upper(w.inventory_id) === upper(r.roll_item_id));
    return { roll_item_id_item_qty: write?.after.item_qty ?? inventoryField(inv,'item_qty') ?? 1,
      roll_item_id_equipped: r.roll_type_1 === 'inventory_deleted' ? '' : inventoryField(inv,'equipped') ?? '',
      roll_item_id_inventory_item_id: inventoryField(inv,'inventory_item_id') ?? null };
  });
  transport.log_item_pictures = verdict.rows.map(r => {
    const inv = inventoryFor(upper(r.roll_item_id));
    return inv ? creaturePictureField(selectCreaturePicture(inv), 'pic_url') ?? null : null;
  });
  const rules = { critical_hits_improved_damage_remarkable: CRIT_CASCADE_RULES.remarkable,
    critical_hits_improved_damage_world_shaking: CRIT_CASCADE_RULES.worldShaking, maimed_on_crit_fortitude_save: CRIT_CASCADE_RULES.maimed };
  transport.log_traces = verdict.rows.map(r => {
    const trace = [...rollTraces.get(r) ?? []];
    const rule = r.house_rule ? rules[r.house_rule] : undefined;
    if (rule) trace.push({ log_banner: 'campaign_house_rule', delta: 0, source: 'campaign_house_rule', label: rule.name,
      kind: 'campaign_house_rule', origin_item_id: rule.id });
    return trace.length ? trace : null;
  });
  const contexts: Row[] = [];
  const cue = (itemId: string, source: string, target: string, visual: Row): void => {
    contexts.push({ roll_id: command.operation_id, roll_item_id: itemId, roll_by: source, roll_to: target,
      encounter_id: command.encounter_id, campaign_id: '', roll_private: command.roll_private,
      roll_vfx: { schema: 'ft.ft3d.magic-cue.v1', itemId, encounterId: command.encounter_id,
        sourceEntityId: source, targetEntityId: target, ...visual } });
  };
  for (const [index, cast] of (verdict.areaCasts ?? []).entries()) {
    cue(cast.itemId, command.actor_id, command.actor_id, { id: `${command.operation_id}:${cast.itemId}:area:${index}`,
      recipe: cast.recipe, area: cast.area });
    contexts[contexts.length - 1]!.area_recipients = cast.recipientUserIds;
  }
  const recipe = record(resolveRollVfxRecipe(command.ability_id, read.definition(command.ability_id) ?? undefined));
  if (recipe?.projectileStyle === 'purchased-wind-push') for (const movement of verdict.forcedMovements ?? [])
    cue(command.ability_id, command.actor_id, movement.targetId, { id: `${command.operation_id}:${command.ability_id}:${command.actor_id}:movement:${movement.targetId}`,
      recipe, movement: { schema: 'ft.ft3d.forced-movement-result.v1', rollId: command.operation_id, ...movement } });
  for (const log of verdict.spawnRows?.logs ?? []) {
    if (!log.arrival || !log.actor_id) continue;
    const child = verdict.spawnRows?.writes.find(w => w.table === 'encounter_creatures' && upper(w.row.encounter_creature_id) === upper(log.actor_id));
    const source = upper(child?.row.controller_encounter_creature_id ?? ecLean(read.member(log.actor_id) ?? {}, 'controller_encounter_creature_id'));
    cue(log.arrival.itemId, source, log.actor_id, { id: `${command.operation_id}:${log.arrival.itemId}:${source}:summon:${log.actor_id}`,
      recipe: log.arrival.recipe, summonPortal: log.arrival.portal });
  }
  verdict.spawnLogRows = (verdict.spawnRows?.logs ?? []).map(log => {
    const child = verdict.spawnRows?.writes.find(w => w.table === 'encounter_creatures' && upper(w.row.encounter_creature_id) === upper(log.actor_id));
    const controller = upper(child?.row.controller_encounter_creature_id ?? ecLean(read.member(log.actor_id ?? '') ?? {}, 'controller_encounter_creature_id'));
    const by = log.kind === 'summoned_creature' && controller ? controller : log.actor_id ?? null;
    return { roll_type_1: log.kind, roll_type_2: '', roll_item_id: null, roll_by: by,
      roll_to: log.kind === 'summoned_creature' && controller ? log.actor_id ?? null : null,
      roll_result: 0, rolls_full: log.name + (log.detail ? ': ' + log.detail : ''),
      roll_private: log.kind === 'janus_unported_skip' || !log.actor_id ? 1 : command.roll_private as 0 | 1 | 2 };
  });
  verdict.cueContexts = contexts;
}
